import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { StarIcon, PlusIcon, TrashIcon, CheckCircleIcon } from "@phosphor-icons/react";

/**
 * Founder Reservations — pre-claim Founder Member status for known social
 * handles / store names that haven't registered yet. When the matching
 * account signs up, it auto-promotes to Founder.
 */
export default function AdminFounderReservations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [handle, setHandle] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/founder-reservations");
      setItems(data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async () => {
    if (!handle.trim()) {
      toast.error("Enter a handle");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/founder-reservations", {
        handle: handle.trim(),
        note: note.trim() || null,
      });
      if (data.just_claimed_user_id) {
        toast.success(`Founder status applied to ${data.raw_handle} (${data.just_claimed_user_id})`);
      } else {
        toast.success(`Reserved ${data.raw_handle} — auto-applies on signup`);
      }
      setHandle("");
      setNote("");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (h) => {
    try {
      await api.delete(`/admin/founder-reservations/${encodeURIComponent(h)}`);
      toast.success(`Removed reservation`);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div
      className="mt-10 border border-amber-200 bg-gradient-to-br from-amber-50 via-pink-50 to-white rounded-2xl overflow-hidden"
      data-testid="admin-founder-reservations-section"
    >
      <div className="p-4 border-b border-amber-200 flex items-center gap-3">
        <StarIcon size={22} weight="fill" className="text-pink-500" />
        <div className="flex-1">
          <div className="font-display font-bold">Founder reservations</div>
          <div className="text-xs text-zinc-600 font-mono">
            Pre-claim ★ Founder status for handles that haven&apos;t signed up yet — auto-applies when they register.
          </div>
        </div>
      </div>

      <div className="p-4 flex items-end gap-2 flex-wrap border-b border-amber-100 bg-white/60">
        <label className="flex-1 min-w-[180px]">
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Handle / email / name</span>
          <Input
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="sprycrystals"
            className="mt-1"
            data-testid="founder-reservation-handle-input"
          />
        </label>
        <label className="flex-1 min-w-[200px]">
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Note (optional)</span>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Onboarded via DM"
            className="mt-1"
            data-testid="founder-reservation-note-input"
          />
        </label>
        <Button
          onClick={submit}
          disabled={busy}
          className="bg-gradient-to-r from-amber-400 to-pink-500 hover:from-amber-500 hover:to-pink-600 text-white gap-2"
          data-testid="founder-reservation-add-btn"
        >
          <PlusIcon size={14} weight="bold" />
          Reserve
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="p-6 text-center text-sm text-zinc-500">
          {loading ? "Loading…" : "No reservations yet. Add one above."}
        </div>
      ) : (
        <div className="divide-y divide-amber-100">
          {items.map((r) => (
            <div
              key={r.handle}
              className="p-3 flex items-center gap-3 flex-wrap"
              data-testid={`founder-reservation-row-${r.handle}`}
            >
              <span
                className="pill bidora-founder-pill-seller text-[10px] inline-flex items-center gap-1"
                title="Reserved Founder seat"
              >
                ★ {r.raw_handle || r.handle}
              </span>
              {r.claimed_by ? (
                <span className="text-[11px] inline-flex items-center gap-1 text-emerald-700 font-bold">
                  <CheckCircleIcon size={12} weight="fill" />
                  Claimed by {r.claimed_by}
                </span>
              ) : (
                <span className="text-[11px] text-amber-700 font-bold uppercase tracking-wider">
                  Awaiting signup
                </span>
              )}
              {r.note && (
                <span className="text-[11px] text-zinc-500 italic flex-1 min-w-0 truncate">
                  {r.note}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => remove(r.handle)}
                className="border-red-300 text-red-600 hover:bg-red-50 ml-auto"
                data-testid={`founder-reservation-delete-${r.handle}`}
              >
                <TrashIcon size={12} className="mr-1" />
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
